import {Chart} from '@antv/g2';
import {useEffect, useRef} from 'react';

/** Definition of a function to create a G2 chart, to be used with "useG2Chart" */
export type G2ChartCreator<T> = (container: HTMLDivElement, data: T[]) => Chart;

/** Creates a G2 chart in a <div> (via "ref"). Will automatically re-render
 * on data change (or "creator" change).
 */
export function useG2Chart<T>(
  /** The reference to <div> element where chart will be displayed */
  ref: React.RefObject<HTMLDivElement>,
  /** Data (used by "creator"). Should be a state. */
  data: T[],
  /** Function that creates the chart. */
  creator: G2ChartCreator<T>,
): React.MutableRefObject<Chart | undefined> {
  const chart = useRef<Chart>();

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    if (chart.current) {
      chart.current.destroy();
      chart.current = undefined;
    }

    chart.current = creator(ref.current, data);

    return () => {
      if (chart.current) {
        chart.current.destroy();
        chart.current = undefined;
      }
    };
  }, [data, creator, ref]);

  return chart;
}
